import { useEffect } from "react";
import { postSessionStart } from "@/helpers/apis";
import { getTenant } from "@/helpers/tenant";

export const SESSION_START_REPORTED_KEY = "altimate.sessionStartReported";

// Survives re-renders and remounts within one page load. A reload starts a new
// document with this reset, which the sessionStorage handoff below covers.
let reportedInThisDocument = false;
let documentLifecycleHandled = false;

/**
 * Forgets that this tab reported, so the next sign-in in the same tab reports
 * again. Called on logout, which is an in-app navigation that keeps the
 * document and its sessionStorage alive.
 */
export const clearSessionStartReported = () => {
  reportedInThisDocument = false;
  try {
    sessionStorage.removeItem(SESSION_START_REPORTED_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
};

/**
 * Carries "already reported" across a reload of this tab. Tabs opened with
 * window.open() start with a copy of the opener's sessionStorage, so the flag
 * lives there only while the tab is between documents: written as this
 * document unloads, consumed by the next load. A live document holds no flag
 * for a child tab to inherit, so the child reports its own session start.
 */
export const storeReportedForNextLoad = () => {
  if (!reportedInThisDocument) return;
  try {
    sessionStorage.setItem(SESSION_START_REPORTED_KEY, "1");
  } catch {
    // Storage unavailable — the next load reports again, which the backend debounces.
  }
};

/**
 * A document restored from the back-forward cache is live again, so it must not
 * leave the flag written on unload for a child tab to inherit.
 */
export const forgetStoredFlagOnRestore = (event: PageTransitionEvent) => {
  if (!event.persisted) return;
  try {
    sessionStorage.removeItem(SESSION_START_REPORTED_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
};

// Installed once per document and never removed: the handoff must still happen
// when the reporter is unmounted at unload time, e.g. after navigating from a
// guarded route to a public one.
const handleDocumentLifecycle = () => {
  if (documentLifecycleHandled) return;
  documentLifecycleHandled = true;
  window.addEventListener("pagehide", storeReportedForNextLoad);
  window.addEventListener("pageshow", forgetStoredFlagOnRestore);
};

/**
 * Tells the backend the signed-in user opened the app, at most once per
 * browser tab. The document is marked before the request is sent and the mark
 * is cleared only on logout, so a failed request is not retried. Every failure
 * is swallowed: this is bookkeeping and must never affect what the user sees.
 */
export const reportSessionStart = () => {
  try {
    // services/auth imports this module, so it is resolved at call time.
    const { isImpersonating } = require("@/services/auth");
    handleDocumentLifecycle();
    // The flag left by the previous document is consumed before any early
    // return, so a live document never holds one for a child tab to inherit.
    const reportedBeforeReload = sessionStorage.getItem(SESSION_START_REPORTED_KEY) !== null;
    sessionStorage.removeItem(SESSION_START_REPORTED_KEY);
    // The tenantless public-admin host has no tenant for the backend to resolve.
    if (!getTenant()) return;
    // An impersonating staff session carries the customer's token; reporting
    // would stamp the customer's last login with staff activity.
    if (isImpersonating()) return;
    if (reportedInThisDocument) return;
    reportedInThisDocument = true;
    if (reportedBeforeReload) return;
    postSessionStart().catch(() => undefined);
  } catch {
    // sessionStorage can throw (privacy mode); skip rather than risk repeat requests.
  }
};

export const SessionStartReporter = () => {
  useEffect(() => {
    reportSessionStart();
  }, []);
  return null;
};
